import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useFormDraft } from "../lib/formDraft";
import {
  Button, Input, Select, FormField, Dialog, DialogCancel,
  Badge, Spinner, Empty, PageHeader,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Card, CardHeader, CardTitle, CardContent,
} from "../components/ui";
import { Plus, Wrench } from "lucide-react";
import { toast } from "sonner";

// ─── Técnicos ─────────────────────────────────────────────────────────────────
function emptyTec() {
  return { nombre: "", especialidad: "AMBOS", telefono: "", email: "" };
}

// ─── Funcionarios ─────────────────────────────────────────────────────────────
function emptyFun() {
  return { nombre: "", ci: "", cargo: "", tipo_salario: "MENSUAL", salario_base: 0, telefono: "" };
}

export default function Tecnicos() {
  const [tab, setTab] = useState("tecnicos");

  return (
    <div className="p-4 sm:p-6">
      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit mb-6">
        {["tecnicos","funcionarios"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors capitalize ${
              tab === t ? "bg-background text-foreground shadow-sm font-medium" : "text-muted-foreground"
            }`}
          >
            {t === "tecnicos" ? "Técnicos" : "Funcionarios"}
          </button>
        ))}
      </div>
      {tab === "tecnicos" ? <TecnicosPanel /> : <FuncionariosPanel />}
    </div>
  );
}

function TecnicosPanel() {
  const [list,   setList]   = useState([]);
  const [load,  setLoading] = useState(true);
  const [open,   setOpen]   = useState(false);
  const [edit,   setEdit]   = useState(null);
  const [form,   setForm]   = useState(emptyTec());
  const [saving, setSaving] = useState(false);
  const draft = useFormDraft("tecnico", form, () => setForm(emptyTec()));

  const reload = useCallback(async () => {
    const r = await api.get("/tecnicos");
    setList(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function openEdit(t) {
    setEdit(t);
    setForm({ nombre: t.nombre, especialidad: t.especialidad, telefono: t.telefono ?? "", email: t.email ?? "" });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, telefono: form.telefono || null, email: form.email || null };
    try {
      if (edit) {
        await api.put(`/tecnicos/${edit.id}`, body);
        toast.success("Técnico actualizado");
      } else {
        await api.post("/tecnicos", body);
        toast.success("Técnico creado");
        draft.clear();
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  if (load) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <>
      <PageHeader title="Técnicos" sub={`${list.length} técnicos`}>
        <Button onClick={() => { setEdit(null); setForm({ ...emptyTec(), ...draft.load() }); setOpen(true); }}>
          <Plus className="w-4 h-4" /> Nuevo técnico
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && <Empty message="Sin técnicos" icon={Wrench} />}
        {list.map((t) => (
          <Card key={t.id} className={!t.activo ? "opacity-50" : ""}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold">{t.nombre}</p>
                  <p className="text-xs text-muted-foreground">{t.especialidad}</p>
                </div>
                <Badge variant={t.activo ? "success" : "secondary"}>
                  {t.activo ? "Activo" : "Inactivo"}
                </Badge>
              </div>
              {t.telefono && <p className="text-sm">{t.telefono}</p>}
              {t.email && <p className="text-sm text-muted-foreground">{t.email}</p>}
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => openEdit(t)}>Editar</Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={async () => {
                    await api.put(`/tecnicos/${t.id}`, { activo: !t.activo });
                    reload();
                  }}
                >
                  {t.activo ? "Desactivar" : "Activar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} values={form} draft={edit ? undefined : draft} title={edit ? "Editar técnico" : "Nuevo técnico"} size="sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Nombre *">
            <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
          </FormField>
          <FormField label="Especialidad">
            <Select value={form.especialidad} onChange={(e) => s("especialidad", e.target.value)}>
              <option value="CHAPA">Chapa</option>
              <option value="PINTURA">Pintura</option>
              <option value="AMBOS">Ambos</option>
            </Select>
          </FormField>
          <FormField label="Teléfono">
            <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} />
          </FormField>
          <FormField label="Email">
            <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} />
          </FormField>
          <div className="flex justify-end gap-2">
            <DialogCancel />
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

function FuncionariosPanel() {
  const [list,   setList]   = useState([]);
  const [load,   setLoading] = useState(true);
  const [open,   setOpen]   = useState(false);
  const [edit,   setEdit]   = useState(null);
  const [form,   setForm]   = useState(emptyFun());
  const [saving, setSaving] = useState(false);
  const draft = useFormDraft("funcionario", form, () => setForm(emptyFun()));

  const reload = useCallback(async () => {
    const r = await api.get("/funcionarios");
    setList(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function openEdit(f) {
    setEdit(f);
    setForm({ nombre: f.nombre, ci: f.ci ?? "", cargo: f.cargo ?? "", tipo_salario: f.tipo_salario, salario_base: f.salario_base, telefono: f.telefono ?? "" });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, ci: form.ci || null, cargo: form.cargo || null, telefono: form.telefono || null };
    try {
      if (edit) {
        await api.put(`/funcionarios/${edit.id}`, body);
        toast.success("Funcionario actualizado");
      } else {
        await api.post("/funcionarios", body);
        toast.success("Funcionario creado");
        draft.clear();
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  if (load) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <>
      <PageHeader title="Funcionarios" sub={`${list.length} funcionarios`}>
        <Button onClick={() => { setEdit(null); setForm({ ...emptyFun(), ...draft.load() }); setOpen(true); }}>
          <Plus className="w-4 h-4" /> Nuevo funcionario
        </Button>
      </PageHeader>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>CI</TableHead>
            <TableHead>Cargo</TableHead>
            <TableHead>Tipo salario</TableHead>
            <TableHead className="text-right">Salario base</TableHead>
            <TableHead>Teléfono</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 && <TableRow><TableCell colSpan={7}><Empty message="Sin funcionarios" /></TableCell></TableRow>}
          {list.map((f) => (
            <TableRow key={f.id} className={!f.activo ? "opacity-50" : ""}>
              <TableCell className="font-medium text-sm">{f.nombre}</TableCell>
              <TableCell className="font-mono text-xs">{f.ci ?? "—"}</TableCell>
              <TableCell className="text-sm">{f.cargo ?? "—"}</TableCell>
              <TableCell className="text-sm">{f.tipo_salario}</TableCell>
              <TableCell className="text-right font-mono text-sm">{f.salario_base?.toLocaleString("es-PY")}</TableCell>
              <TableCell className="text-sm">{f.telefono ?? "—"}</TableCell>
              <TableCell>
                <button onClick={() => openEdit(f)} className="text-xs text-primary hover:underline">Editar</button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} values={form} draft={edit ? undefined : draft} title={edit ? "Editar funcionario" : "Nuevo funcionario"} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Nombre *" className="col-span-full">
              <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
            </FormField>
            <FormField label="CI">
              <Input value={form.ci} onChange={(e) => s("ci", e.target.value)} />
            </FormField>
            <FormField label="Cargo">
              <Input value={form.cargo} onChange={(e) => s("cargo", e.target.value)} />
            </FormField>
            <FormField label="Tipo salario">
              <Select value={form.tipo_salario} onChange={(e) => s("tipo_salario", e.target.value)}>
                <option value="MENSUAL">Mensual</option>
                <option value="QUINCENAL">Quincenal</option>
                <option value="POR_TRABAJO">Por trabajo</option>
              </Select>
            </FormField>
            <FormField label="Salario base">
              <Input type="number" min="0" value={form.salario_base} onChange={(e) => s("salario_base", parseFloat(e.target.value) || 0)} />
            </FormField>
            <FormField label="Teléfono" className="col-span-full">
              <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} />
            </FormField>
          </div>
          <div className="flex justify-end gap-2">
            <DialogCancel />
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
